<script setup lang="ts" generic="T extends string | number">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { nxRadioGroupInjectionKey, type NxRadioItemBaseProps } from './radio-internal';

const props = defineProps<NxRadioItemBaseProps<T>>();

const injectedGroup = inject(nxRadioGroupInjectionKey);
if (!injectedGroup) {
  throw new Error('NxRadio must be used inside NxRadioGroup');
}
const group = injectedGroup;

const checked = computed(() => group.modelValue.value === props.value);
const isDisabled = computed(() => group.disabled.value || props.disabled === true);
// Roving tabindex: only the group's active item is in the tab sequence, so the
// whole group is a single tab stop and arrow keys move focus between items.
const tabindex = computed(() => (group.activeValue.value === props.value ? 0 : -1));
// A unique name per item switches off native radio grouping — the group handles
// navigation/selection so arrow keys move focus without selecting.
const inputName = computed(() => `${group.name.value ?? 'radio'}__${String(props.value)}`);

const inputRef = ref<HTMLInputElement | null>(null);

// Fires on native activation from a mouse click on the label. Keyboard
// selection (Space/Enter) is handled by the group's keydown handler.
async function onChange() {
  group.select(props.value);
  /**
   * ⚠️ **The DOM has to be pulled back onto the model, or a REFUSED value kills
   * the item** (I-1372). The input is a group of ONE — the unique name above
   * switches off native grouping — so the browser's activation leaves it
   * `checked` even when the model does not take the value, and a radio alone in
   * its group can never be unchecked by clicking it again. Per HTML the
   * activation behaviour fires `change` only when the checkedness CHANGED, so
   * from then on every further click on that card is silent: the write is never
   * attempted again, not even after the user has removed what blocked it.
   *
   * Vue cannot repair it on its own: the `:checked` binding never changed value
   * (false → false), so there is nothing for it to patch, and the stale DOM
   * state survives every re-render.
   *
   * After the tick, because `select` may reach an async model — the write then
   * lands later and Vue's own patch takes it from here.
   */
  await nextTick();
  if (inputRef.value) inputRef.value.checked = checked.value;
}

let unregister: () => void = () => {};
onMounted(() => {
  unregister = group.register({
    value: props.value,
    disabled: () => isDisabled.value,
    focus: () => inputRef.value?.focus(),
  });
});
onBeforeUnmount(() => unregister());
</script>

<template>
  <label
    class="nx-radio"
    :class="{ checked, disabled: isDisabled }"
    :aria-label="props.label"
    :title="props.label"
  >
    <input
      ref="inputRef"
      type="radio"
      class="nx-radio-input"
      :name="inputName"
      :value="value"
      :checked="checked"
      :disabled="isDisabled"
      :tabindex="tabindex"
      @change="onChange"
    />
    <slot
      :disabled="isDisabled"
      :checked="checked"
      :save-state="group.saveState.value"
      :error-message="group.errorMessage.value"
      :save-fade-out="group.saveFadeOut.value"
    ></slot>
  </label>
</template>

<style scoped>
.nx-radio {
  /* Anchor for the save-status badge, which the visual items slot in as a
     SIBLING of their (fading) visual so it never fades along. */
  position: relative;
  width: fit-content;
  height: fit-content;
  cursor: pointer;
}

/* Disabled (or the group is loading): override the label and the slotted visual
   (card/swatch set their own `cursor: pointer`) to the blocked cursor. Keyed on
   the `.disabled` class, which is correctly toggled — unlike a `disabled`
   attribute, which Vue renders as `disabled="false"` on a <div> (always present). */
.nx-radio.disabled,
.nx-radio.disabled :slotted(*) {
  cursor: not-allowed;
}

.nx-radio-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

/* The native input is visually hidden, so mirror its keyboard focus onto the
   slotted visual (dot / card / swatch). `:focus-visible` shows the ring on
   keyboard/arrow navigation only, not on mouse selection. `outline` follows the
   visual's own border-radius (rounded card, circular swatch) and does not
   clash with the swatch's box-shadow ring. The save badge is also slotted —
   exclude it, the ring belongs on the visual only. */
.nx-radio-input:focus-visible ~ :slotted(*:not(.save-badge)) {
  outline: calc(var(--nxt-hairline) * 3) solid var(--nxt-radio-ring);
  outline-offset: 0.125em;
}
</style>
